"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useWatch, type Control, type UseFormSetValue } from "react-hook-form";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useSearchLocationsByInput } from "@/components/modules/search/api/useSearchLocationsByInput";
import { useLocationsById } from "@/hooks/QueryHooks/useLocationsById";
import { SearchTypes } from "@/constants/filters";
import { LocationBadges } from "@/components/molecules/LocationMap/LocationBadges";
import LocationMapCaller from "@/components/molecules/LocationMap/LocationMapCaller";
import { cn } from "@/lib/utils";
import { clampMapSelection, isMaxLocationsReached, toggleCodeWithCap } from "./locationCap";
type SearchLocation = {
  code: string | null;
  name: string;
  proxyName?: string;
  administrativeLevel: string;
  proxyAdministrativeLevel?: string;
};
type MultiLocationSearchContentProps = {
  name: string;
  maxLocations?: number;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  // Which picker this sheet shows. The mobile flow opens a text-search sheet
  // from the input and a map sheet from the thumbnail (mirrors the supply form).
  showSearch?: boolean;
  showMapPicker?: boolean;
};
export function MultiLocationSearchContent(t0) {
  const $ = _c(20);
  const {
    name,
    maxLocations,
    control,
    setValue,
    showSearch: t1,
    showMapPicker: t2
  } = t0;
  const showSearch = t1 === undefined ? true : t1;
  const showMapPicker = t2 === undefined ? true : t2;
  const locationCodes = useWatch({
    control,
    name
  }) ?? [];
  const [queryInput, setQueryInput] = useState("");
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [nameCache, setNameCache] = useState(t3);
  const t = useTranslations("modules.listings.addRoomListing.houseDetails");
  let t4;
  if ($[1] !== queryInput) {
    t4 = {
      input: queryInput,
      businessType: SearchTypes.RENT_ROOM
    };
    $[1] = queryInput;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    data: searchResults,
    isLoading: isSearching
  } = useSearchLocationsByInput(t4);
  const {
    data: locationNamesData
  } = useLocationsById({
    locations: locationCodes,
    enabled: locationCodes.length > 0
  });
  let t5;
  let t6;
  if ($[3] !== locationNamesData) {
    t5 = () => {
      if (!locationNamesData?.data) {
        return;
      }
      setNameCache(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const loc of locationNamesData.data) {
          if (loc.code && !next.has(loc.code)) {
            next.set(loc.code, loc.name);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    t6 = [locationNamesData];
    $[3] = locationNamesData;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  const locations = (searchResults?.data ?? searchResults ?? []) as Array<SearchLocation>;
  let t7;
  if ($[6] !== nameCache) {
    t7 = code => ({
      code,
      name: nameCache.get(code) ?? code
    });
    $[6] = nameCache;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const selectedLocations = locationCodes.map(t7);
  const handleToggleCode = function handleToggleCode(code_0, locationName) {
    if (locationName) {
      setNameCache(prev_0 => {
        if (prev_0.has(code_0)) {
          return prev_0;
        }
        const next_0 = new Map(prev_0);
        next_0.set(code_0, locationName);
        return next_0;
      });
    }
    const newCodes = toggleCodeWithCap(locationCodes, code_0, maxLocations);
    if (newCodes === null) {
      return;
    }
    setValue(name, newCodes, {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  const handleRemoveCode = function handleRemoveCode(code_1) {
    setValue(name, locationCodes.filter(c => c !== code_1), {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  let t8;
  if ($[8] !== name || $[9] !== setValue) {
    t8 = function handleClearAll() {
      setValue(name, [], {
        shouldValidate: true,
        shouldDirty: true
      });
    };
    $[8] = name;
    $[9] = setValue;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleClearAll = t8;
  const handleMapSelectionChange = function handleMapSelectionChange(codes, nameByCode) {
    if (nameByCode) {
      setNameCache(prev_1 => {
        let changed_0 = false;
        const next_1 = new Map(prev_1);
        for (const [code_2, locName] of nameByCode) {
          if (!next_1.has(code_2)) {
            next_1.set(code_2, locName);
            changed_0 = true;
          }
        }
        return changed_0 ? next_1 : prev_1;
      });
    }
    const limitedCodes = clampMapSelection(locationCodes, codes, maxLocations);
    setValue(name, limitedCodes, {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  const selectedNames = new Map(selectedLocations.map(_temp));
  const visibleLocations = locations.filter(_temp2);
  const showHint = queryInput.length <= 2;
  const isMaxReached = isMaxLocationsReached(locationCodes.length, maxLocations);
  const t9 = showSearch && <Command shouldFilter={false} className="bg-transparent flex-1 min-h-0 px-2"><CommandInput placeholder={t("searchLocations")} onValueChange={setQueryInput} className="h-12" />{isMaxReached && <p className="px-2 pt-2 text-xs text-faint-foreground">{t("maxLocationsReached", {
        max: maxLocations ?? 0
      })}</p>}<CommandList className="flex-1 max-h-none">{isSearching && queryInput.length > 2 ? <div className="flex items-center justify-center py-6"><Loader2 className="size-4 animate-spin text-faint-foreground" /></div> : <>{!showHint && <CommandEmpty>{t("noLocationsFound")}</CommandEmpty>}{visibleLocations.length > 0 && <CommandGroup>{visibleLocations.map(loc_0 => {
            const isSelected = locationCodes.includes(loc_0.code);
            const isDisabled = isMaxReached && !isSelected;
            return <CommandItem key={loc_0.code} value={`${loc_0.proxyName ?? loc_0.name} ${loc_0.code}`} disabled={isDisabled} onSelect={() => handleToggleCode(loc_0.code, loc_0.proxyName ?? loc_0.name)} className={cn("cursor-pointer py-4 rounded-[10px] text-base [&_span]:text-base", isDisabled && "opacity-40 cursor-not-allowed")}><div className="flex items-center gap-2 w-full"><div className="flex-1 min-w-0"><span className="font-medium truncate">{loc_0.proxyName ?? loc_0.name}</span><span className="text-faint-foreground ml-2 text-xs">{loc_0.proxyAdministrativeLevel ?? loc_0.administrativeLevel}</span></div><Check className={cn("ml-auto size-4 shrink-0 text-primary", isSelected ? "opacity-100" : "opacity-0")} /></div></CommandItem>;
          })}</CommandGroup>}</>}</CommandList></Command>;
  const t10 = showMapPicker && <div className="px-2 pb-3 shrink-0 flex-1 min-h-0"><div className="h-full min-h-64 overflow-hidden rounded-2xl border border-input"><LocationMapCaller businessType={SearchTypes.RENT_ROOM} selectedCodes={locationCodes} onSelectionChange={handleMapSelectionChange} selectedNames={selectedNames} showBadges={false} showCount={false} /></div></div>;
  let t11;
  if ($[11] !== handleClearAll || $[12] !== handleRemoveCode || $[13] !== maxLocations || $[14] !== selectedLocations) {
    t11 = selectedLocations.length > 0 && <div className="px-4 pb-5 shrink-0"><LocationBadges locations={selectedLocations} onRemove={handleRemoveCode} onClearAll={handleClearAll} single={maxLocations === 1} /></div>;
    $[11] = handleClearAll;
    $[12] = handleRemoveCode;
    $[13] = maxLocations;
    $[14] = selectedLocations;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}
function _temp2(l_0) {
  return l_0.code !== null;
}
function _temp(l) {
  return [l.code, l.name];
}